import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, Typography, Checkbox } from '../../../../../../components';
import { FolderOpenOutlined } from '@ant-design/icons';
import { CreatableSelect } from '../../../../../../components/CreatableSelect';
import '../CreateAndManageModal.scss';
import './EditApplicationModal.scss';

const { Option } = Select;
const { Title, Text } = Typography;

/**
 * EditApplicationModal - Modal for editing an existing application
 * Includes options to update application name, assign/unassign move group,
 * and remove application assignment from all machines
 */
export const EditApplicationModal = ({ open, onCancel, onApply, application = null, moveGroups = [], users = [] }) => {
    const [form] = Form.useForm();
    const [removeAssignment, setRemoveAssignment] = useState(false);
    const [unassignFromMoveGroup, setUnassignFromMoveGroup] = useState(false);
    const moveGroupId = Form.useWatch('moveGroupId', form);

    const hasCurrentMoveGroup = application?.move_group_ids?.length > 0;
    const hasAssignedVMs = application?.machines > 0 || application?.vms?.length > 0;

    useEffect(() => {
        if (open && application) {
            // Pre-populate form with existing application data
            // application.move_group_ids is an array, but app should only have one move group
            const moveGroupId = application.move_group_ids?.[0] || undefined;

            form.setFieldsValue({
                name: application.name || '',
                moveGroupId: moveGroupId,
            });
        } else if (open && !application) {
            // Reset form when modal opens without an application
            form.resetFields();
        }
    }, [open, application, form, moveGroups]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            const payload = {
                id: application?.id,
                name: values.name,
                moveGroupId: unassignFromMoveGroup ? null : values.moveGroupId,
                removeAssignment,
                unassignFromMoveGroup,
            };

            onApply(payload);
            handleCancel();
        } catch (error) {
            console.error('Validation failed:', error);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setRemoveAssignment(false);
        setUnassignFromMoveGroup(false);
        onCancel();
    };

    const handleRemoveAssignmentChange = (e) => {
        setRemoveAssignment(e.target.checked);
        if (e.target.checked) {
            // Clear form fields when removing assignment
            form.setFieldsValue({
                moveGroupId: undefined,
            });
        }
    };

    return (
        <Modal
            className="create-manage-modal edit-application-modal"
            title={
                <Title level={4} className="modal-title">
                    Edit Application
                </Title>
            }
            open={open}
            onCancel={handleCancel}
            footer={null}
            width={520}
            destroyOnHidden
        >
            <Text type="secondary" className="modal-description">
                Edit selected application details.
            </Text>

            <Form form={form} layout="vertical" className="manage-modal-form">
                {/* Application Name */}
                <Form.Item
                    label={
                        <span>
                            Name <span className="required"></span>
                        </span>
                    }
                    name="name"
                    rules={[{ required: true, message: 'Application name is required' }]}
                >
                    <Input placeholder="Enter Application name" disabled={removeAssignment} />
                </Form.Item>

                {/* Unassign Checkbox - Only show if application has VMs assigned */}
                {hasAssignedVMs && (
                    <div className="remove-assignment-section">
                        <Checkbox checked={removeAssignment} onChange={handleRemoveAssignmentChange}>
                            Unassign (Clear all)
                        </Checkbox>
                        <Text type="secondary" className="remove-assignment-description">
                            Remove this app assignment from assigned machines.
                        </Text>
                    </div>
                )}

                {/* Move Group Selection */}
                <Form.Item
                    label={
                        <span>
                            <FolderOpenOutlined className="icon-with-label" /> Move Group
                        </span>
                    }
                    name="moveGroupId"
                >
                    <CreatableSelect
                        key={moveGroupId}
                        selectPlaceholder="Select or create Move Group"
                        addItemPlaceholder="Create new Move Group"
                        options={moveGroups}
                        value={moveGroupId}
                        onChange={(value) => form.setFieldsValue({ moveGroupId: value })}
                        disabled={removeAssignment || unassignFromMoveGroup}
                    />
                </Form.Item>

                {/* Unassign from Move Group Checkbox - Only show if app currently has a move group */}
                {hasCurrentMoveGroup && (
                    <div className="unassign-move-group-section" style={{ marginBottom: '16px' }}>
                        <Checkbox
                            checked={unassignFromMoveGroup}
                            onChange={(e) => {
                                setUnassignFromMoveGroup(e.target.checked);
                                if (e.target.checked) {
                                    form.setFieldsValue({ moveGroupId: undefined });
                                }
                            }}
                            disabled={removeAssignment}
                        >
                            Unassign from Move Group
                        </Checkbox>
                    </div>
                )}

                {/* Footer Buttons */}
                <div className="button-group">
                    <Button shape="round" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button shape="round" type="primary" onClick={handleSubmit}>
                        Apply
                    </Button>
                </div>
            </Form>
        </Modal>
    );
};
